import type { CSSProperties, RefObject } from "react";
import { Button } from "../../components/Button";
import type { TourStep, TourStepInteraction } from "../../types/tours";

/** Used when the anchor gives no short name of its own, so the instruction always reads. */
const FALLBACK_CONTROL_NAME = "the highlighted control";

/**
 * The instruction, in the bubble rather than only in the spotlight, because a coach who cannot see
 * the highlight has to be told that the control itself also moves the tour on.
 *
 * Next is always there too. Coaches asked for it: a step that could only be passed by pressing the
 * highlighted control read as the tour being stuck. Next on such a step presses the control for
 * them (see `TourOverlay`), so both ways land on the same next step with the same popup open.
 */
const clickToContinuePrompt = (controlName: string | null, isLast: boolean) =>
  `Click ${controlName ?? FALLBACK_CONTROL_NAME} or ${isLast ? "Finish" : "Next"} to continue`;

interface TourBubbleProps {
  step: TourStep;
  /** Defaulted by the overlay. `ClickToContinue` is the one that changes what the bubble offers. */
  interaction: TourStepInteraction;
  /**
   * What the anchor element calls itself, resolved by the overlay from the element it is
   * spotlighting. Null when it gives no short, usable name, and the instruction falls back to
   * "the highlighted control".
   */
  anchorLabel?: string | null;
  /**
   * The step hands the page to the coach: `inert` is lifted, the shade passes clicks, and Tab and
   * Escape go to whatever they opened. It is exactly the inverse of `aria-modal` (see below), so
   * it comes from the overlay rather than being re-derived from `interaction` here.
   */
  isInteractive: boolean;
  stepIndex: number;
  stepCount: number;
  /** "new" runs carry a header saying these are additions, not the whole tour. */
  isNewSteps: boolean;
  /** A system admin previewing a draft. Marked on screen so a draft is never mistaken for live. */
  isPreview?: boolean;
  /** Null renders a centered card. */
  position: { top: number; left: number } | null;
  bubbleRef: RefObject<HTMLDivElement | null>;
  titleRef: RefObject<HTMLHeadingElement | null>;
  titleId: string;
  descriptionId: string;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const TourBubble = ({
  step,
  interaction,
  anchorLabel = null,
  isInteractive,
  stepIndex,
  stepCount,
  isNewSteps,
  isPreview = false,
  position,
  bubbleRef,
  titleRef,
  titleId,
  descriptionId,
  onNext,
  onBack,
  onSkip,
}: TourBubbleProps) => {
  const isLast = stepIndex === stepCount - 1;
  const clickToContinue = interaction === "ClickToContinue";
  const style: CSSProperties | undefined = position
    ? { top: position.top, left: position.left }
    : undefined;

  return (
    <div
      ref={bubbleRef}
      role="dialog"
      /*
        Only while the page really is shielded. `aria-modal` tells a screen reader that everything
        outside this element does not exist, and on an interactive step that is the opposite of
        what the tour has just done: `inert` is lifted, the shade passes clicks, Tab and Escape are
        handed back, and the control the step is asking the coach to press is *outside* the bubble.
        Claiming it there takes the target out of the virtual buffer and hides the panel an
        `OpenAnchor` step opened for them to look at. The flag tracks `inert` exactly, so the two
        can never say different things about the same page.

        Nothing depends on the bubble matching `[aria-modal="true"]`: the only query for it is
        `openHostDialogs`, which excludes the overlay by `data-tour-overlay` before it looks.
      */
      aria-modal={isInteractive ? undefined : "true"}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabIndex={-1}
      className={`tour-bubble${position ? "" : " tour-bubble--centered"}`}
      style={style}
      data-testid="tour-bubble"
    >
      {isPreview && (
        <p className="tour-bubble__preview" data-testid="tour-preview-marker">
          Preview
        </p>
      )}
      {isNewSteps && <p className="tour-bubble__eyebrow">New on this page</p>}
      <h2 ref={titleRef} id={titleId} className="tour-bubble__title" tabIndex={-1}>
        {step.title}
      </h2>
      <div id={descriptionId} className="tour-bubble__description">
        <p className="tour-bubble__body">{step.body}</p>
        {/*
          A polite live region, not only part of the description. The bubble is one long-lived
          dialog: a description is announced when focus enters it, and moving to the next step only
          moves focus to the title inside it. Without this a coach hears the new step's title and
          nothing about the control that also moves the tour on. The region itself stays mounted and the paragraph is
          keyed by step, so two `ClickToContinue` steps in a row still announce the second.
        */}
        <div role="status" className="tour-bubble__prompt-region">
          {clickToContinue && (
            <p
              key={`${step.key}:prompt`}
              className="tour-bubble__prompt"
              data-testid="tour-click-prompt"
            >
              {clickToContinuePrompt(anchorLabel, isLast)}
            </p>
          )}
        </div>
        <p className="tour-bubble__counter" data-testid="tour-step-counter">
          Step {stepIndex + 1} of {stepCount}
        </p>
      </div>
      <div className="tour-bubble__actions">
        <Button
          type="button"
          variant="secondary"
          className="tour-bubble__skip"
          onClick={onSkip}
          data-testid="tour-skip-button"
        >
          Skip tour
        </Button>
        <div className="tour-bubble__nav">
          {stepIndex > 0 && (
            <Button
              type="button"
              variant="secondary"
              className="tour-bubble__button"
              onClick={onBack}
              data-testid="tour-back-button"
            >
              Back
            </Button>
          )}
          <Button
            type="button"
            className="tour-bubble__button"
            onClick={onNext}
            data-testid="tour-next-button"
          >
            {isLast ? "Finish" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};
